import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Spin,
  Typography,
  Button,
  Table,
  Tooltip,
  message,
  Tag,
  DatePicker,
} from "antd";
import { Link, useParams } from "react-router-dom";
import { Application, ApplicationAction } from "../../model_gen/application";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import moment from "moment/moment";
import { Config } from "../../model_gen/config";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import CustomSwitch from "../shared/switch/CustomSwitch";
import {
  DownOutlined,
  RightOutlined,
  CopyOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { RequestsService } from "../../services/RequestsService";
import RequestView from "../shared/RequestView";
import { ColumnsType } from "antd/es/table";
import { ChatCompletionRequest } from "../../model_gen/chat";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { ReactComponent as AiStars } from "../../assets/svg/ai-creation.svg";
import { IntentService } from "../../services/IntentService";
import { IntentGroup } from "../../model_gen/intent";

dayjs.extend(utc);

const ApplicationActionOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { applicationId, actionId } = useParams<{
    applicationId: string;
    actionId: string;
  }>();
  const [application, setApplication] = useState<Application | null>(null);
  const [config, setConfig] = useState<Config>();
  const [applicationAction, setApplicationAction] =
    useState<ApplicationAction | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsCurrentPage, setRequestsCurrentPage] = useState(1);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const requestsByPageRef = useRef<Record<number, any[]>>({});
  const [initialLoadDate] = useState<number>(Date.now());
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);

  const getConfig = () => {
    ApplicationService.getApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      null,
      apiKey,
    ).subscribe(
      (config: Config) => {
        setConfig(config);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };
  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true,
  ) => {
    setConfig((prevConfig: Config | undefined) => {
      if (!prevConfig) {
        return config;
      }
      return {
        ...prevConfig,
        ...config,
      };
    });
    if (!save) {
      return;
    }
    ApplicationService.setApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      config,
      loadingHandler,
      apiKey,
    ).subscribe(
      (config: Config) => {
        setConfig(config);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  const resetConfig = () => {
    setConfig(undefined);
    ApplicationService.resetApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      null,
      apiKey,
    ).subscribe(
      () => {
        getConfig();
      },
      (error: any) => {
        console.error(error);
      },
    );
  };
  const fetchRequests = (page: number) => {
    if (requestsByPageRef.current[page] && !startDate && !endDate) {
      setRequests(requestsByPageRef.current[page]);
      return;
    }

    const offset = (page - 1) * 5;
    setRequestsLoading(true);

    const start = startDate
      ? dayjs(startDate).startOf("day").utc().valueOf()
      : -1;
    const endTemp = endDate
      ? dayjs(endDate).add(1, "day").startOf("day").utc().valueOf()
      : initialLoadDate;
    const end = endTemp > initialLoadDate ? initialLoadDate : endTemp;

    RequestsService.getApplicationActionRequests(
      Number(applicationId),
      Number(actionId),
      start,
      end,
      offset,
      5,
      setRequestsLoading,
      apiKey,
    ).subscribe(
      (data: any) => {
        setRequests(data.items || []);
        setRequestsTotal(data.total || 0);
        requestsByPageRef.current[page] = data.items || [];
        setRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setRequestsLoading(false);
      },
    );
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  useEffect(() => {
    if (applicationId) {
      ApplicationService.getApplication(
        Number(applicationId),
        setIsLoading,
        apiKey,
      ).subscribe(
        (app: Application) => {
          setApplication(app);
          ApplicationService.getApplicationAction(
            Number(applicationId),
            Number(actionId),
            null,
            apiKey,
          ).subscribe((action: ApplicationAction) => {
            setApplicationAction(action);
            getConfig();
          });
        },
        (error: any) => {
          console.error(error);
        },
      );
      if (applicationId && actionId) {
        IntentService.getIntentGroupByApplicationAndIntent(Number(applicationId), Number(actionId), () => {}, apiKey).subscribe(
          (intentGroup: IntentGroup) => {
            setIntentGroup(intentGroup);
          },
          (error: any) => {
            console.error(error);
          },
        );
      }
    }
  }, [applicationId, actionId]);

  useEffect(() => {
    if (applicationId && actionId && expandedPanels.has("requests")) {
      if (!requestsByPageRef.current[requestsCurrentPage]) {
        fetchRequests(requestsCurrentPage);
      } else {
        setRequests(requestsByPageRef.current[requestsCurrentPage]);
      }
    }
  }, [applicationId, actionId, requestsCurrentPage, expandedPanels]);

  useEffect(() => {
    if ((!startDate || startDate == null) && (!endDate || endDate == null)) {
      requestsByPageRef.current = {};
      setRequestsCurrentPage(1);
      fetchRequests(1);
    }
  }, [startDate, endDate]);

  const togglePanel = (panel: string, checked: boolean) => {
    const newSet = new Set(expandedPanels);
    if (checked) {
      newSet.add(panel);
      if (
        panel === "requests" &&
        !requestsByPageRef.current[requestsCurrentPage]
      ) {
        fetchRequests(requestsCurrentPage);
      }
    } else {
      newSet.delete(panel);
    }
    setExpandedPanels(newSet);
  };

  if (isLoading) {
    return (
      <Spin
        size="large"
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      />
    );
  }

  const requestColumns: ColumnsType<ChatCompletionRequest> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      key: "tags",
      width: "30%",
      render: (_, request: ChatCompletionRequest) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_request_meta as string) || "{}",
          );
          const tags = metaData.tags || [];
          return (
            <Space wrap>
              {tags.map((tag: string) => (
                <Tag key={tag} bordered={true}>
                  {tag}
                </Tag>
              ))}
            </Space>
          );
        } catch (e) {
          return null;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "35%",
      align: "right",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            {requestId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_, record: ChatCompletionRequest) => (
        <Link
          to={`/application/${record.application_id}/session/${encodeURIComponent(record.session_id)}/request/${record.request_id}`}
          target="_blank">
          <Tooltip
            title={<RequestView request={record} apiKey={apiKey} />}
            placement="left"
            overlayStyle={{ maxWidth: "600px", width: "600px" }}>
            <Button type="link" icon={<EyeOutlined />}>
              View
            </Button>
          </Tooltip>
        </Link>
      ),
    },
  ];
  return (
    <Card className="applications-container applications-wrapper">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
            <Link to="/" style={{ color: "#828282" }}>
              Applications {" / "}{" "}
            </Link>
            <Link
              to={`/application/${applicationId}`}
              style={{ color: "#828282" }}>
              {application?.application_name}
              {" / "}{" "}
            </Link>
            {applicationAction?.action_type}
          </Typography>
        </Col>
        <Col>
          <Link
            to={`/intent-group/${applicationAction?.intent_group_id}`}>
            <Button type="primary">View Intent Group</Button>
          </Link>
        </Col>
      </Row>
      <Card className="overview-wrapper" style={{ marginBottom: 24 }} bordered>
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Descriptions colon={false} column={12} layout="vertical">
              <Descriptions.Item label="Intent Name" span={3}>
                {applicationAction?.action_type}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div style={{ display: "flex", alignItems: "start" }}>
                    Tags
                    <AiStars
                      style={{
                        width: "12px",
                        height: "12px",
                        color: "#8c8c8c",
                      }}
                    />
                  </div>
                }
                span={3}>
                {(() => {
                  try {
                    const tags = JSON.parse(
                      (applicationAction?.meta?.metadata?.tags as string) ||
                        "[]",
                    );
                    return (
                      <Space wrap style={{ whiteSpace: "pre-wrap" }}>
                        {tags.map((tag: string) => (
                          <Tag key={tag} bordered={true}>
                            {tag}
                          </Tag>
                        ))}
                      </Space>
                    );
                  } catch (e) {
                    return "-";
                  }
                })()}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div style={{ display: "flex", alignItems: "start" }}>
                    Description
                    <AiStars
                      style={{
                        width: "12px",
                        height: "12px",
                        color: "#8c8c8c",
                      }}
                    />
                  </div>
                }
                span={6}>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {(applicationAction?.meta?.metadata?.intent as string) || "-"}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={24}>
            <Descriptions colon={false} column={12} layout="vertical">
              <Descriptions.Item label="Notifications" span={3}>
                <CustomSwitch
                  className="custom-switch"
                  checked={applicationAction?.notifications_enabled}
                  onChange={(checked) => {
                    const updateAction = () => {
                      setApplicationAction((prevState) => {
                        if (!prevState) return prevState;
                        return {
                          ...prevState,
                          notifications_enabled: checked,
                        };
                      });
                    };
                    if (applicationAction) {
                      if (checked) {
                        ApplicationService.enableApplicationActionNotifications(
                          applicationAction.application_id,
                          applicationAction.id,
                          null,
                          apiKey,
                        ).subscribe(
                          () => {
                            updateAction();
                          },
                          (error: any) => {
                            console.error(error);
                          },
                        );
                      } else {
                        ApplicationService.disableApplicationActionNotifications(
                          applicationAction.application_id,
                          applicationAction.id,
                          null,
                          apiKey,
                        ).subscribe(
                          () => {
                            updateAction();
                          },
                          (error: any) => {
                            console.error(error);
                          },
                        );
                      }
                    }
                  }}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Last Activity" span={3}>
                {moment(applicationAction?.last_activity).format(
                  "YYYY-MM-DD hh:mm:ss A",
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Requests" span={6}>
                {applicationAction?.request_count}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>
      <Card className="overview-wrapper-panel" bordered>
        <ConfigurationPanel
          intentGroupId={intentGroup?.id}
          config={config}
          updateConfig={updateConfig}
          resetConfig={resetConfig}
          showSystemPrompt={false}
        />
      </Card>
      {applicationAction && (
        <Space
          direction="vertical"
          size="small"
          style={{ width: "100%", marginTop: "24px" }}>
          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: "8px" }}>
            <Row align="middle">
              <Col flex="none" style={{ marginRight: "8px" }}>
                <Button
                  type="text"
                  icon={
                    expandedPanels.has("requests") ? (
                      <DownOutlined />
                    ) : (
                      <RightOutlined />
                    )
                  }
                  onClick={() =>
                    togglePanel("requests", !expandedPanels.has("requests"))
                  }
                />
              </Col>
              <Col>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Requests
                </Typography.Title>
              </Col>
            </Row>
          </Row>
          {expandedPanels.has("requests") && (
            <>
              <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 16 }}>
                <Col>
                  <Space>
                    <DatePicker
                      placeholder="Start Date"
                      onChange={(date) => {
                        setStartDate(date);
                      }}
                      value={startDate}
                      disabledDate={(current) =>
                        endDate ? current && current > endDate : false
                      }
                    />
                    <DatePicker
                      placeholder="End Date"
                      onChange={(date) => {
                        setEndDate(date);
                      }}
                      value={endDate}
                      disabledDate={(current) =>
                        startDate ? current && current < startDate : false
                      }
                    />
                    <Button
                      onClick={handleClearFilters}
                      disabled={!endDate && !startDate}>
                      Clear Filters
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        requestsByPageRef.current = {};
                        setRequestsCurrentPage(1);
                        fetchRequests(1);
                      }}
                      disabled={!startDate && !endDate}>
                      Search
                    </Button>
                  </Space>
                </Col>
              </Row>
              <Table
                rowKey="request_id"
                columns={requestColumns}
                dataSource={requests}
                loading={requestsLoading}
                pagination={{
                  current: requestsCurrentPage,
                  pageSize: 5,
                  total: requestsTotal,
                  onChange: (page) => setRequestsCurrentPage(page),
                  showSizeChanger: false,
                }}
              />
            </>
          )}
        </Space>
      )}
    </Card>
  );
};

export default ApplicationActionOverview;
